import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, PenTool, ShieldCheck, RotateCcw, CheckCircle2, AlertTriangle, FileText } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Public signature page — no auth required.
 * Customer receives a magic link like /sign/{token}, sees a preview of the
 * document (quote or calibration report), draws their signature on a canvas
 * and submits. The backend stores IP + UA + SHA-256 + timestamp as legal
 * evidence under ESIGN Act / UETA.
 */
export default function PublicSign() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [doc, setDoc] = useState(null);
  const [signerName, setSignerName] = useState("");
  const [signerEmail, setSignerEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [signed, setSigned] = useState(null); // { signed_at, image_sha256, verification_token }
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const hasStrokeRef = useRef(false);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/public/sign/${token}`)
      .then((r) => {
        setDoc(r.data);
        if (r.data.already_signed) {
          setSigned({ signed_at: r.data.signed_at, signer_name: r.data.signer_name });
        }
        if (r.data.customer_name) setSignerName(r.data.customer_name);
      })
      .catch((e) => setError(e.response?.data?.detail || "Invalid or expired link"))
      .finally(() => setLoading(false));
  }, [token]);

  // Canvas drawing handlers (mouse + touch)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#0f172a";
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const getPos = (evt) => {
      const rect = canvas.getBoundingClientRect();
      const t = evt.touches ? evt.touches[0] : evt;
      return {
        x: ((t.clientX - rect.left) / rect.width) * canvas.width,
        y: ((t.clientY - rect.top) / rect.height) * canvas.height,
      };
    };
    const start = (evt) => {
      evt.preventDefault();
      drawingRef.current = true;
      const p = getPos(evt);
      ctx.beginPath();
      ctx.moveTo(p.x, p.y);
    };
    const move = (evt) => {
      if (!drawingRef.current) return;
      evt.preventDefault();
      const p = getPos(evt);
      ctx.lineTo(p.x, p.y);
      ctx.stroke();
      hasStrokeRef.current = true;
    };
    const end = () => { drawingRef.current = false; };
    canvas.addEventListener("mousedown", start);
    canvas.addEventListener("mousemove", move);
    canvas.addEventListener("mouseup", end);
    canvas.addEventListener("mouseleave", end);
    canvas.addEventListener("touchstart", start, { passive: false });
    canvas.addEventListener("touchmove", move, { passive: false });
    canvas.addEventListener("touchend", end);
    return () => {
      canvas.removeEventListener("mousedown", start);
      canvas.removeEventListener("mousemove", move);
      canvas.removeEventListener("mouseup", end);
      canvas.removeEventListener("mouseleave", end);
      canvas.removeEventListener("touchstart", start);
      canvas.removeEventListener("touchmove", move);
      canvas.removeEventListener("touchend", end);
    };
  }, [doc, signed]);

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    hasStrokeRef.current = false;
  };

  const submit = async () => {
    if (!signerName || signerName.trim().length < 2) {
      toast.error("Please type your full name");
      return;
    }
    if (!hasStrokeRef.current) {
      toast.error("Please draw your signature on the box");
      return;
    }
    const canvas = canvasRef.current;
    const image_base64 = canvas.toDataURL("image/png");
    setSubmitting(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/public/sign/${token}`, {
        image_base64,
        signer_name: signerName.trim(),
        signer_email: signerEmail || null,
      });
      setSigned({
        signed_at: r.data.signed_at,
        signer_name: signerName.trim(),
        image_sha256: r.data.image_sha256,
        verification_token: r.data.verification_token,
      });
      toast.success("Document signed — thank you!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit signature");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030712] text-slate-100 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-blue-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#030712] text-slate-100 flex items-center justify-center p-6">
        <div className="max-w-md w-full rounded-2xl border border-rose-500/30 bg-rose-500/5 p-8 text-center">
          <AlertTriangle className="w-10 h-10 text-rose-400 mx-auto mb-3" />
          <h1 className="text-xl font-semibold">Link unavailable</h1>
          <p className="text-slate-400 text-sm mt-2">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030712] text-slate-100 py-8 px-4" data-testid="public-sign-page">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Brand header */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center font-bold text-lg">T</div>
          <div>
            <div className="font-display text-lg font-semibold">{doc?.company_name || "TrueLine"}</div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500 font-bold">Calibration Platform</div>
          </div>
        </div>

        {/* Doc preview */}
        <DocumentPreview doc={doc} />

        {signed ? (
          <SignedReceipt doc={doc} signed={signed} />
        ) : (
          <div className="rounded-2xl border border-blue-500/30 bg-blue-500/5 p-6 space-y-4" data-testid="sign-form">
            <div className="flex items-center gap-2">
              <PenTool className="w-5 h-5 text-blue-300" />
              <h2 className="font-display text-xl font-medium">Sign this document</h2>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wide font-bold text-slate-400">Your full name *</Label>
                <Input
                  className="mt-1.5 bg-[#030712] border-slate-800"
                  value={signerName} onChange={(e) => setSignerName(e.target.value)}
                  placeholder="Enter your legal full name"
                  data-testid="signer-name-input"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide font-bold text-slate-400">Email (optional)</Label>
                <Input
                  className="mt-1.5 bg-[#030712] border-slate-800"
                  type="email"
                  value={signerEmail} onChange={(e) => setSignerEmail(e.target.value)}
                  placeholder="you@email.com"
                  data-testid="signer-email-input"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label className="text-xs uppercase tracking-wide font-bold text-slate-400">Draw your signature *</Label>
                <button onClick={clearCanvas} className="text-[11px] text-slate-400 hover:text-blue-300 flex items-center gap-1" data-testid="clear-signature-btn">
                  <RotateCcw className="w-3 h-3" /> Clear
                </button>
              </div>
              <canvas
                ref={canvasRef}
                width={600}
                height={180}
                className="w-full rounded-lg border-2 border-dashed border-slate-700 bg-white touch-none cursor-crosshair"
                data-testid="signature-canvas"
              />
            </div>

            <div className="rounded-lg bg-slate-900/40 border border-slate-800 p-3 text-[11px] text-slate-400 leading-relaxed">
              <strong className="text-slate-200 flex items-center gap-1.5 mb-1"><ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />Legal notice</strong>
              By clicking &quot;Submit Signature&quot;, I acknowledge under the U.S. ESIGN Act (15 U.S.C. §7001) and UETA that my electronic signature has the same legal effect as a handwritten signature and I accept the terms of this document. My IP address and timestamp will be captured for audit purposes.
            </div>

            <Button
              onClick={submit} disabled={submitting}
              className="w-full bg-emerald-600 hover:bg-emerald-500 h-12 text-base font-semibold"
              data-testid="submit-signature-btn"
            >
              {submitting ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <PenTool className="w-5 h-5 mr-2" />}
              Submit Signature
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}


function DocumentPreview({ doc }) {
  if (!doc) return null;
  if (doc.kind === "quote") {
    return (
      <div className="rounded-2xl border border-slate-800 bg-[#0B1120] p-6 space-y-4" data-testid="doc-preview">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">Estimate / Quote</div>
            <h1 className="font-display text-2xl font-semibold mt-1">{doc.quote_number || "Quote"}</h1>
            <div className="text-xs text-slate-400 mt-0.5">Issued {doc.issued_date || ""}</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Total</div>
            <div className="font-mono text-2xl text-emerald-400">${(doc.total || 0).toFixed(2)}</div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-3 text-sm">
          <div className="rounded-lg bg-slate-900/40 border border-slate-800 p-3">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Customer</div>
            <div className="text-slate-200">{doc.customer_name || "—"}</div>
          </div>
          <div className="rounded-lg bg-slate-900/40 border border-slate-800 p-3">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Vehicle</div>
            <div className="text-slate-200">{doc.vehicle || "—"}</div>
            {doc.vin && <div className="font-mono text-[11px] text-blue-300 mt-0.5">{doc.vin}</div>}
          </div>
        </div>

        <div className="rounded-lg bg-slate-900/40 border border-slate-800 p-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2">Line Items</div>
          <div className="space-y-1.5">
            {(doc.lines || []).map((l, i) => (
              <div key={i} className="flex items-center justify-between text-sm py-1.5 border-b border-slate-800/60 last:border-0">
                <div className="text-slate-200">{l.op_label || l.description || l.name || "Operation"}</div>
                <div className="font-mono text-emerald-400">${(l.amount || l.price || l.unit_price || 0).toFixed(2)}</div>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between pt-3 mt-2 border-t border-slate-700">
            <div className="text-xs text-slate-400">Subtotal</div>
            <div className="font-mono text-sm text-slate-200">${(doc.subtotal || 0).toFixed(2)}</div>
          </div>
          {doc.tax > 0 && (
            <div className="flex items-center justify-between text-xs text-slate-400 mt-1">
              <div>Tax</div>
              <div className="font-mono text-slate-300">${(doc.tax || 0).toFixed(2)}</div>
            </div>
          )}
          <div className="flex items-center justify-between pt-2 mt-1">
            <div className="text-sm font-semibold text-slate-200">Total</div>
            <div className="font-mono text-lg text-emerald-400">${(doc.total || 0).toFixed(2)}</div>
          </div>
        </div>

        {doc.notes && (
          <div className="rounded-lg bg-slate-900/40 border border-slate-800 p-3 text-sm text-slate-300 whitespace-pre-line">
            {doc.notes}
          </div>
        )}
      </div>
    );
  }

  // calibration_report
  return (
    <div className="rounded-2xl border border-slate-800 bg-[#0B1120] p-6 space-y-4" data-testid="doc-preview">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">Calibration Report</div>
          <h1 className="font-display text-2xl font-semibold mt-1">{doc.vehicle || "Vehicle"}</h1>
          {doc.vin && <div className="font-mono text-xs text-blue-300 mt-0.5">{doc.vin}</div>}
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Projected Total</div>
          <div className="font-mono text-2xl text-emerald-400">${(doc.total_projected_price || 0).toFixed(2)}</div>
        </div>
      </div>

      {(doc.sections || []).map((sec, i) => (
        <div key={i} className="rounded-lg bg-slate-900/40 border border-slate-800 p-3">
          <div className="text-[10px] uppercase tracking-wider text-blue-300 font-bold mb-2">{sec.label}</div>
          <div className="space-y-1.5">
            {(sec.cards || []).map((c, j) => (
              <div key={j} className="flex items-center justify-between text-sm py-1.5 border-b border-slate-800/60 last:border-0">
                <div>
                  <div className="text-slate-200">{c.name}</div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-0.5">{c.status}</div>
                </div>
                <div className="font-mono text-emerald-400">${(c.price || 0).toFixed(2)}</div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}


function SignedReceipt({ doc, signed }) {
  return (
    <div className="rounded-2xl border border-emerald-500/40 bg-emerald-500/5 p-8 text-center" data-testid="signed-receipt">
      <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto mb-3" />
      <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-300 font-bold">Signature captured</div>
      <h2 className="font-display text-2xl font-semibold mt-2">Thank you, {signed.signer_name}!</h2>
      <p className="text-slate-400 text-sm mt-2">
        Your electronic signature has been recorded and is legally binding under the ESIGN Act and UETA.
      </p>

      <div className="mt-6 rounded-lg bg-slate-900/40 border border-slate-800 p-4 text-left text-xs space-y-2">
        <div className="flex items-start gap-2">
          <FileText className="w-3.5 h-3.5 shrink-0 mt-0.5 text-slate-400" />
          <div>
            <div className="text-slate-400">Document</div>
            <div className="text-slate-200 font-medium">
              {doc?.kind === "quote" ? doc?.quote_number : `Calibration Report · ${doc?.vehicle || ""}`}
            </div>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <ShieldCheck className="w-3.5 h-3.5 shrink-0 mt-0.5 text-slate-400" />
          <div>
            <div className="text-slate-400">Signed at</div>
            <div className="text-slate-200 font-medium">{new Date(signed.signed_at).toLocaleString()}</div>
          </div>
        </div>
        {signed.image_sha256 && (
          <div className="flex items-start gap-2">
            <PenTool className="w-3.5 h-3.5 shrink-0 mt-0.5 text-slate-400" />
            <div>
              <div className="text-slate-400">Signature hash (SHA-256)</div>
              <div className="text-slate-200 font-mono text-[10px] break-all">{signed.image_sha256}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
